"use client";

import { use, useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { collabWsUrl } from "@workspace/collaboration/ws-url";
import {
  CollaborationProvider,
  useCollaboration,
} from "@workspace/collaboration/provider";
import type { Editor } from "@tiptap/react";
import { SidebarPanel } from "@/components/sidebar/sidebar";
import { Outline } from "@/components/navigator/outline";
import { DocumentTags } from "@/components/editor/document-tags";
import { Input } from "@workspace/ui/components/input";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@workspace/ui/components/dialog";
import {
  AppFrame,
  AppFrameClose,
  AppFrameRightSlot,
  AppFrameToggle,
  useAppFrame,
} from "@workspace/ui/components/app-frame";
import {
  Loader2,
  MoreHorizontal,
  FileText,
  BookOpen,
  Eye,
  Pencil,
  PanelLeftClose,
} from "lucide-react";
import useSWR from "swr";
import { apiClient } from "@/lib/api-client";
import { useDocTheme } from "@/lib/theme/use-doc-theme";
import { useIsMobile } from "@/lib/use-is-mobile";
import { DocumentStatus } from "@/components/status/document-status";
import { useMounted } from "@/lib/use-mounted";
import { looksLikeShortId, shortIdFromFrontmatter } from "@/lib/short-id";

// Dynamic import with SSR disabled — TipTap editor is client-only
const DocumentEditor = dynamic(
  () =>
    import("@/components/editor/editor").then((mod) => mod.DocumentEditor),
  { ssr: false, loading: () => <EditorSkeleton /> }
);

function EditorSkeleton() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

interface EditorPageProps {
  params: Promise<{ docId: string }>;
}

export default function EditorPage({ params }: EditorPageProps) {
  const { docId: urlId } = use(params);
  const { authenticated, loading: authLoading } = useAuth({
    requireAuth: true,
  });
  const [title, setTitle] = useState("");
  const mounted = useMounted();
  const isMobile = useIsMobile();

  // View mode hides the editing chrome (toolbar, title input, tags, sidebar)
  // and makes the editor read-only. It defaults ON for mobile, where the
  // chrome eats most of the viewport, and OFF on desktop. `null` until the
  // breakpoint is known so the toolbar never flashes on a phone; the user's
  // own toggle wins from then on.
  const [viewModeOverride, setViewModeOverride] = useState<boolean | null>(null);
  // Declared before `doc` is fetched, so read-only-ness is folded in below.
  const preferredViewMode = viewModeOverride ?? isMobile ?? false;

  const {
    data: doc,
    error: docError,
    mutate: mutateDoc,
  } = useSWR<{
    id?: string;
    shortId?: string | null;
    title?: string;
    metadata?: string | null;
    initialContent?: string | null;
    frontmatter?: string | null;
    /** The caller's access (lib/access). A viewer's edits would be dropped. */
    access?: "owner" | "editor" | "viewer";
    /** Set on a lower stage's copy of a prod engineering doc (tdd-0117). */
    mirroredFrom?: string;
    /** The branch whose worktree last wrote this engineering doc; null for main. */
    sourceBranch?: string | null;
    /** Set while this doc's platform edits cannot be merged into its file (tdd-0117 P5). */
    syncConflict?: string | null;
  }>(
    mounted && authenticated ? `/api/documents/${urlId}` : null,
    fetcher,
    // The URL is rewritten to the short id below, which changes this key. Keep
    // showing the same document across that swap, or `docId` would blank for a
    // frame and tear down the collab session.
    { keepPreviousData: true }
  );

  // Short URLs (`/tdd-096`, `/tdd-0096`). The API resolves either form, but
  // the collab session is keyed on the platform id. Mounting under the URL
  // param would open a NEW, empty Y.Doc named `tdd-096`. So a short URL waits
  // for the fetch to name the real id, and a platform-id URL (the common case)
  // mounts immediately without waiting.
  // A viewer (collaborator or public reader) is held in view mode: the collab
  // server drops their updates, so an editable editor would let them type
  // changes that silently vanish.
  const readOnly = doc?.access === "viewer";
  const viewMode = readOnly || preferredViewMode;

  const isShortUrl = looksLikeShortId(urlId);
  const docId = isShortUrl ? doc?.id : urlId;

  // Show the friendly form in the address bar: `/tdd-96` → `/tdd-0096`, and a
  // nanoid link to an engineering doc → its short id. `shortId` is only set when
  // it round-trips to this exact document (ids clash, see resolve-doc.ts).
  // `replaceState` rather than `router.replace` so nothing re-renders the route.
  const shortId = doc?.shortId ?? null;
  useEffect(() => {
    if (!shortId || urlId === shortId || doc?.id !== docId) return;
    const { search, hash } = window.location;
    window.history.replaceState(window.history.state, "", `/${shortId}${search}${hash}`);
  }, [shortId, urlId, doc?.id, docId]);

  // Document theme — resolves per-doc override ?? user default ?? default
  const themeState = useDocTheme(docId ?? urlId, doc?.metadata);

  // The live TipTap instance, for the outline. Set by the editor once built.
  const [editor, setEditor] = useState<Editor | null>(null);

  // Seed the editable title from the server whenever the fetched value changes,
  // adjusted DURING RENDER rather than in an effect. An effect paints one frame
  // with the stale title before correcting it, which shows as a visible flicker
  // in the header on load; React re-runs this component on the set below before
  // touching the DOM. Local edits still win until the server value itself moves.
  const [lastDocTitle, setLastDocTitle] = useState(doc?.title);
  if (doc?.title !== lastDocTitle) {
    setLastDocTitle(doc?.title);
    if (doc?.title) setTitle(doc.title);
  }

  // Debounced title autosave — fires 2s after user stops typing
  const titleTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Takes the title to save rather than reading a ref mirrored during render
  // (which React flags — `react-hooks/refs`). Both call sites are event-driven,
  // so the value they close over is already the current one.
  const saveTitle = useCallback(
    async (t: string) => {
      if (!docId || !t.trim() || t === doc?.title) return;
      await apiClient.patch(`/api/documents/${docId}`, { title: t });
      mutateDoc();
    },
    [doc?.title, docId, mutateDoc]
  );

  const handleTitleChange = useCallback(
    (value: string) => {
      setTitle(value);
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
      titleTimerRef.current = setTimeout(() => saveTitle(value), 2000);
    },
    [saveTitle]
  );

  // Clean up timer
  useEffect(() => {
    return () => {
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    };
  }, []);

  const handleAiAction = useCallback(
    (action: string, content: string) => {
      window.dispatchEvent(
        new CustomEvent("docs:ai-command", {
          detail: { action, content },
        })
      );
    },
    []
  );

  if (!mounted || authLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-var(--global-header-height,54px))]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  if (docError && !doc) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-[calc(100dvh-var(--global-header-height,54px))] text-center px-4">
        <p className="font-medium">Document not found</p>
        <p className="text-sm text-muted-foreground">
          Nothing matches <code className="font-mono">{urlId}</code>.
        </p>
        <Link href="/" className="text-sm text-primary hover:underline">
          Back to documents
        </Link>
      </div>
    );
  }

  // Hold the editor until the platform id is known. `documentName` names the
  // Y.Doc, so mounting under a short id, even for one render, would open an
  // empty document and could persist it under the wrong name.
  if (!docId) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-var(--global-header-height,54px))]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <CollaborationProvider
      documentName={docId}
      wsUrl={collabWsUrl(process.env.NEXT_PUBLIC_WS_URL)}
      // Focus and input reporting for platform presence (tdd-0079). Without
      // it the server sees an open socket and cannot tell someone reading this
      // document from someone who left it open behind a dozen other tabs — so
      // every reader would resolve `passive` and get a bell badge for the
      // paragraph in front of them.
      reportAttention
    >
      {/* adr-0023: both sides are supplementary. The document panel is
          structured (five tabs, a properties table) and docks first; the
          outline is a list and gives way first. AppFrame decides widths,
          breakpoints and drawers. The title bar holds the toggles, so the
          frame's own mobile bar is off. */}
      <AppFrame
        yield="left"
        left={{
          role: "supplementary",
          content: "list",
          title: "Outline",
          node: <OutlinePane editor={editor} />,
        }}
        right={{ role: "supplementary", content: "structured", title: "Document panel" }}
        mobileBar={false}
      >
        <TitleBar
          title={title}
          onTitleChange={handleTitleChange}
          onTitleBlur={() => saveTitle(title)}
          docId={docId}
          urlId={shortId ?? docId}
          metadata={doc?.metadata ? JSON.parse(doc.metadata) : null}
          viewMode={viewMode}
          onToggleViewMode={() => {
            if (!readOnly) setViewModeOverride(!viewMode);
          }}
        />
        {doc?.sourceBranch && (
          <div
            data-slot="branch-banner"
            className="border-b bg-muted/50 px-4 py-2 text-sm text-muted-foreground"
          >
            Draft on branch <code className="font-mono text-foreground">{doc.sourceBranch}</code>: not merged
            to main yet.
          </div>
        )}
        {doc?.syncConflict && (
          <div
            data-slot="sync-conflict-banner"
            className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive"
          >
            The file and the platform both changed this section, so these edits are not in git yet. They
            are kept here; the next edit to either side retries.
          </div>
        )}
        {doc?.mirroredFrom && (
          <div
            data-slot="mirror-banner"
            className="flex flex-wrap items-center gap-x-2 border-b bg-muted/50 px-4 py-2 text-sm text-muted-foreground"
          >
            <span>Engineering docs are edited on prod; this is a read-only copy.</span>
            <a href={doc.mirroredFrom} className="font-medium text-foreground underline underline-offset-4">
              Open on prod
            </a>
          </div>
        )}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <DocumentEditor
            documentId={docId}
            initialContent={doc?.initialContent}
            onAiAction={handleAiAction}
            theme={themeState.theme}
            viewMode={viewMode}
            onEditor={setEditor}
            selfDocRef={shortIdFromFrontmatter(doc?.frontmatter)}
          />
        </div>
        <DocumentPanelSlot
          documentId={docId}
          hasFrontmatter={!!doc?.frontmatter}
          themeState={themeState}
          viewMode={viewMode}
        />
      </AppFrame>
    </CollaborationProvider>
  );
}

// --- Side content (laid out by AppFrame, adr-0023) ---

/**
 * The outline, as AppFrame's left side: docked beside the editor on wide
 * screens, a drawer below. Following a heading from the drawer closes it — the
 * reader went where they meant to — while docked it stays put.
 */
function OutlinePane({ editor }: { editor: Editor | null }) {
  const { left } = useAppFrame();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between pl-4 pr-2 h-11 shrink-0">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Outline
        </span>
        <AppFrameClose side="left" className="h-8 w-8" title="Hide outline">
          <PanelLeftClose className="h-4 w-4" />
        </AppFrameClose>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Outline
          editor={editor}
          onNavigate={left.docked ? undefined : () => left.setOpen(false)}
        />
      </div>
    </div>
  );
}

/**
 * Properties, history, commits, theme and AI, as AppFrame's right side. It
 * brings its own tab header (`chrome="own"`).
 *
 * Beside the editor it is editing chrome, so view mode hides it there. As a
 * drawer it stays reachable in view mode: properties and history are as
 * useful to a reader as to an editor.
 */
function DocumentPanelSlot({
  documentId,
  hasFrontmatter,
  themeState,
  viewMode,
}: {
  documentId: string;
  hasFrontmatter: boolean;
  themeState: ReturnType<typeof useDocTheme>;
  viewMode: boolean;
}) {
  const { right } = useAppFrame();
  if (viewMode && right.docked) return null;
  return (
    <AppFrameRightSlot id={DOCUMENT_PANEL} title="Document panel" chrome="own">
      <SidebarPanel
        documentId={documentId}
        hasFrontmatter={hasFrontmatter}
        themeState={themeState}
        closeMode={right.docked ? "collapse" : "dismiss"}
        onClose={() => right.setOpen(false)}
      />
    </AppFrameRightSlot>
  );
}

const DOCUMENT_PANEL = "document-panel";

// --- Title bar with save status & "Save as template" ---

interface DocumentMetadata {
  sourceUrl?: string;
  author?: string | null;
  publishedDate?: string | null;
  siteName?: string | null;
}

function TitleBar({
  title,
  onTitleChange,
  onTitleBlur,
  docId,
  urlId,
  metadata,
  viewMode,
  onToggleViewMode,
}: {
  title: string;
  onTitleChange: (v: string) => void;
  onTitleBlur: () => void;
  docId: string;
  /** How this doc is addressed in links: its short id when it has one. */
  urlId: string;
  metadata?: DocumentMetadata | null;
  viewMode: boolean;
  onToggleViewMode: () => void;
}) {
  const { saveStatus } = useCollaboration();
  // The panel is hidden beside the editor in view mode (see
  // DocumentPanelSlot), so its toggle would open nothing there.
  const { right } = useAppFrame();
  const panelAvailable = !(viewMode && right.docked);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setTemplateSaving(true);
    try {
      // Fetch the current editor content via a custom event that the editor
      // component listens for. Alternatively, we can get the TipTap JSON
      // directly — but the simplest approach is to fetch the raw Yjs state
      // and create a template from the document's content.
      // For templates we store TipTap JSON, so we'll use a different approach:
      // read the editor's HTML from the DOM and use that, or better yet,
      // dispatch an event to get the JSON.
      //
      // Actually, the cleanest way: the editor content is in the Yjs doc
      // which is synced. We can get the HTML by reading from the API,
      // or we can read the prosemirror content from the DOM.
      //
      // Simplest: fetch the rendered content from the editor DOM and convert.
      // But let's use a different approach — dispatch an event and have the
      // editor respond with its JSON content.
      const content = await getEditorJSON();
      await apiClient.post("/api/templates", {
        name: templateName,
        content,
        category: templateCategory || null,
      });
      setTemplateDialogOpen(false);
      setTemplateName("");
      setTemplateCategory("");
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 border-b">
        <AppFrameToggle side="left" className="h-8 w-8 shrink-0 -ml-1 justify-center px-0" title="Outline" />

        {/* The title takes whatever the row has spare and ellipsises rather than
            pushing anything off the end. `flex-1` claims the slack the controls
            do not use; `basis-0` stops its content length from biasing the
            split. The `min-w-` floor matters more than it looks: with a plain
            `min-w-0` the collections could squeeze the title to literally zero
            width on a narrow viewport, so the document lost its name before the
            tags lost a single badge. */}
        {viewMode ? (
          <h1 className="min-w-[6rem] flex-1 basis-0 truncate text-lg font-semibold">
            {title || "Untitled"}
          </h1>
        ) : (
          <Input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={onTitleBlur}
            onKeyDown={(e) => e.key === "Enter" && onTitleBlur()}
            className="min-w-[6rem] flex-1 basis-0 border-0 text-lg font-semibold h-auto p-0 focus-visible:ring-0 text-ellipsis"
            placeholder="Untitled"
          />
        )}

        {/* Collections, then status — both editing chrome, so both go in view
            mode, but the status indicator stays (it is information, not a
            control). */}
        {!viewMode && <DocumentTags docId={docId} inline />}

        <DocumentStatus saveStatus={saveStatus} />

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleViewMode}
          className="h-8 w-8 shrink-0"
          aria-pressed={viewMode}
          aria-label={viewMode ? "Edit document" : "View document"}
          title={viewMode ? "Edit" : "View"}
        >
          {viewMode ? (
            <Pencil className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>

        {panelAvailable && (
          <AppFrameToggle
            side="right"
            slot={DOCUMENT_PANEL}
            className="h-8 w-8 shrink-0 justify-center px-0"
            title="Properties & history"
          />
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link href={`/${urlId}/read`}>
                <BookOpen className="h-4 w-4 mr-2" />
                Read / print view
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setTemplateName(title || "");
                setTemplateDialogOpen(true);
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Save as template
            </DropdownMenuItem>
            {metadata?.sourceUrl && (
              <div className="px-2 py-1.5 border-t mt-1">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Source
                </p>
                <a
                  href={metadata.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline truncate block"
                >
                  {metadata.siteName || new URL(metadata.sourceUrl).hostname}
                </a>
                {metadata.author && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    by {metadata.author}
                  </p>
                )}
                {metadata.publishedDate && (
                  <p className="text-[11px] text-muted-foreground">
                    {new Date(metadata.publishedDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

      </div>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as template</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Template name</label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Product Requirements Document"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">
                Category{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </label>
              <Input
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                placeholder="e.g. product, engineering, design"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTemplateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveAsTemplate}
              disabled={!templateName.trim() || templateSaving}
            >
              {templateSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Get the TipTap editor's JSON content via a request/response event pair.
 * The editor component listens for "docs:get-editor-json" and responds
 * with "docs:editor-json-response".
 */
function getEditorJSON(): Promise<Record<string, unknown>> {
  return new Promise((resolve) => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      window.removeEventListener("docs:editor-json-response", handler);
      resolve(detail);
    };
    window.addEventListener("docs:editor-json-response", handler);
    window.dispatchEvent(new CustomEvent("docs:get-editor-json"));

    // Fallback if no response in 1s
    setTimeout(() => {
      window.removeEventListener("docs:editor-json-response", handler);
      resolve({ type: "doc", content: [] });
    }, 1000);
  });
}
